/**
 * Operations dashboard summary metrics — concise “what do I need to know”
 * aggregates for ops widgets. Pure builders + thin loaders; no UI.
 */

import { supabase } from "../supabase";
import {
  buildAccountsReceivableDashboard,
  loadReceivableInvoices,
} from "./accountsReceivable";
import { loadEstimates, resolveEstimateStatus } from "./estimates";
import { loadFinancialPosition } from "./financePosition";
import { shouldSkipSupabase } from "./guestMode";
import {
  loadScopedActiveProjects,
  loadScopedClients,
} from "./operationalQueryAdapters";
import {
  computePayrollLiabilityTotals,
  getPayrollWeekBoundsFromSettings,
  sumHours,
} from "./payrollHelpers";
import { loadScopedAccountsPayableTimelogs } from "./scopedQueries";
import { isProjectArchived } from "./projectVisibility";

function daysAgoIso(days, asOf = new Date()) {
  const d = new Date(asOf);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

function isWithinDays(isoValue, days, asOf = new Date()) {
  if (!isoValue) return false;
  const ts = new Date(isoValue).getTime();
  if (!Number.isFinite(ts)) return false;
  return ts >= new Date(daysAgoIso(days, asOf)).getTime();
}

function formatMoney(amount) {
  const value = Number(amount);
  const safe = Number.isFinite(value) ? value : 0;
  try {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
    }).format(safe);
  } catch {
    return `$${safe.toFixed(2)}`;
  }
}

function formatSignedMoney(amount) {
  const value = Number(amount);
  const safe = Number.isFinite(value) ? value : 0;
  try {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      signDisplay: "exceptZero",
    }).format(safe);
  } catch {
    const prefix = safe > 0 ? "+" : "";
    return `${prefix}$${safe.toFixed(2)}`;
  }
}

function formatHours(hours) {
  const value = Number(hours);
  const safe = Number.isFinite(value) ? value : 0;
  return `${safe.toFixed(1)}`;
}

export function buildAccountsReceivableSummaryMetrics(dashboard) {
  const aging = dashboard?.aging || {};
  return [
    {
      key: "outstanding",
      label: "Outstanding",
      value: formatMoney(dashboard?.outstandingReceivables),
    },
    { key: "current", label: "Current", value: formatMoney(aging.current) },
    { key: "days_30", label: "1–30", value: formatMoney(aging.days_30) },
    { key: "days_60", label: "31–60", value: formatMoney(aging.days_60) },
    { key: "days_90", label: "61–90", value: formatMoney(aging.days_90) },
    {
      key: "days_90_plus",
      label: "90+",
      value: formatMoney(aging.days_90_plus),
    },
  ];
}

export function buildAccountsPayableSummaryMetrics({
  unpaidEntries = 0,
  payrollLiability = 0,
} = {}) {
  return [
    {
      key: "unpaid",
      label: "Unpaid Entries",
      value: String(unpaidEntries),
    },
    {
      key: "liability",
      label: "Payroll Liability",
      value: formatMoney(payrollLiability),
    },
  ];
}

export function buildFinancialPositionSummaryMetrics(position) {
  const moneyInToday = Number(position?.moneyInToday) || 0;
  const moneyOutToday = Number(position?.moneyOutToday) || 0;
  const netChangeToday = moneyInToday - moneyOutToday;

  return [
    {
      key: "current",
      label: "Current Cash",
      value: formatMoney(position?.currentCash),
    },
    {
      key: "available",
      label: "Available Cash",
      value: formatMoney(position?.availableCash),
    },
    {
      key: "reserved",
      label: "Reserved Cash",
      value: formatMoney(position?.reservedCash),
    },
    {
      key: "sales_tax_held",
      label: "Sales Tax Held",
      value: formatMoney(position?.salesTaxHeld),
    },
    {
      key: "in",
      label: "Money In Today",
      value: formatMoney(moneyInToday),
    },
    {
      key: "out",
      label: "Money Out Today",
      value: formatMoney(moneyOutToday),
    },
    {
      key: "net",
      label: "Net Change Today",
      value: formatSignedMoney(netChangeToday),
    },
  ];
}

export function buildEstimatePipelineSummaryMetrics(countsByStatus = {}) {
  const draft = countsByStatus.draft || 0;
  const ready = countsByStatus.ready || 0;
  const sent = countsByStatus.sent || 0;
  const approved = countsByStatus.approved || 0;
  const active = draft + ready + sent + approved;
  return [
    { key: "active", label: "Active Estimates", value: String(active) },
    { key: "ready", label: "Ready", value: String(ready) },
    { key: "sent", label: "Sent", value: String(sent) },
    { key: "approved", label: "Approved", value: String(approved) },
  ];
}

export function buildClientsSummaryMetrics(clients = [], asOf = new Date()) {
  const rows = clients || [];
  const recentlyAdded = rows.filter((row) =>
    isWithinDays(row.created_at, 7, asOf),
  ).length;
  const newClients = rows.filter((row) =>
    isWithinDays(row.created_at, 30, asOf),
  ).length;
  return [
    { key: "total", label: "Total Clients", value: String(rows.length) },
    {
      key: "recent",
      label: "Recently Added",
      value: String(recentlyAdded),
    },
    { key: "new", label: "New Clients", value: String(newClients) },
  ];
}

export function buildProjectsSummaryMetrics(projects = [], asOf = new Date()) {
  const active = (projects || []).filter((project) => !isProjectArchived(project));
  const needsAttention = active.filter(
    (project) => !isWithinDays(project.updated_at || project.created_at, 14, asOf),
  ).length;
  const recentlyUpdated = active.filter((project) =>
    isWithinDays(project.updated_at || project.created_at, 7, asOf),
  ).length;
  return [
    {
      key: "active",
      label: "Active Projects",
      value: String(active.length),
    },
    {
      key: "attention",
      label: "Needs Attention",
      value: String(needsAttention),
    },
    {
      key: "recent",
      label: "Recently Updated",
      value: String(recentlyUpdated),
    },
  ];
}

export function buildTimeLogsSummaryMetrics({
  hoursThisWeek = 0,
  employeesWorking = 0,
  pendingApproval = 0,
  estimatedPayroll = 0,
} = {}) {
  return [
    {
      key: "hours",
      label: "Hours This Week",
      value: formatHours(hoursThisWeek),
    },
    {
      key: "working",
      label: "Employees Working",
      value: String(employeesWorking),
    },
    {
      key: "pending",
      label: "Pending Approval",
      value: String(pendingApproval),
    },
    {
      key: "payroll",
      label: "Estimated Payroll",
      value: formatMoney(estimatedPayroll),
    },
  ];
}

export function countEstimatesByStatus(rows = []) {
  const counts = {};
  for (const row of rows) {
    const status = resolveEstimateStatus(row?.status);
    counts[status] = (counts[status] || 0) + 1;
  }
  return counts;
}

export async function loadAccountsReceivableOpsSummary(actor) {
  if (shouldSkipSupabase(actor)) {
    return buildAccountsReceivableSummaryMetrics({});
  }
  const rows = await loadReceivableInvoices(actor);
  return buildAccountsReceivableSummaryMetrics(
    buildAccountsReceivableDashboard(rows),
  );
}

export async function loadAccountsPayableOpsSummary(actor, orgSettings) {
  if (shouldSkipSupabase(actor)) {
    return buildAccountsPayableSummaryMetrics();
  }
  const data = await loadScopedAccountsPayableTimelogs(supabase, actor, {});
  const unpaidRows = (data || []).filter(
    (row) => row.payroll_status !== "paid",
  );
  const liability = computePayrollLiabilityTotals(unpaidRows, orgSettings);
  return buildAccountsPayableSummaryMetrics({
    unpaidEntries: unpaidRows.length,
    payrollLiability: liability.grossPay,
  });
}

export async function loadFinancialPositionOpsSummary(actor) {
  if (shouldSkipSupabase(actor)) {
    return buildFinancialPositionSummaryMetrics({});
  }
  const position = await loadFinancialPosition(supabase);
  return buildFinancialPositionSummaryMetrics(position);
}

export async function loadEstimatePipelineOpsSummary(actor) {
  if (shouldSkipSupabase(actor)) {
    return buildEstimatePipelineSummaryMetrics({});
  }
  const rows = await loadEstimates(actor);
  return buildEstimatePipelineSummaryMetrics(countEstimatesByStatus(rows));
}

export async function loadClientsOpsSummary(actor) {
  if (shouldSkipSupabase(actor)) {
    return buildClientsSummaryMetrics([]);
  }
  const rows = await loadScopedClients(
    supabase,
    actor,
    "client_id, client_name, created_at, client_status",
  );
  return buildClientsSummaryMetrics(rows);
}

export async function loadProjectsOpsSummary(actor, context = {}) {
  if (shouldSkipSupabase(actor)) {
    return buildProjectsSummaryMetrics([]);
  }
  const rows = await loadScopedActiveProjects(
    supabase,
    actor,
    context,
    "project_id, project_name, status, is_active, created_at, updated_at, organization_id",
  );
  return buildProjectsSummaryMetrics(rows);
}

export async function loadTimeLogsOpsSummary(actor, orgSettings) {
  if (shouldSkipSupabase(actor)) {
    return buildTimeLogsSummaryMetrics();
  }

  const { weekStartStr, weekEndStr } = getPayrollWeekBoundsFromSettings(
    new Date(),
    orgSettings,
  );

  const [weekLogsResult, sessionsResult] = await Promise.all([
    supabase
      .from("time_logs")
      .select(
        "id, total_hours, hourly_rate, date_iso, supabase_user_id, payroll_status, approval_status, is_archived",
      )
      .gte("date_iso", weekStartStr)
      .lte("date_iso", weekEndStr)
      .eq("is_archived", false)
      .neq("approval_status", "archived"),
    supabase
      .from("active_sessions")
      .select("id, supabase_user_id, status")
      .eq("status", "active"),
  ]);

  if (weekLogsResult.error) {
    throw weekLogsResult.error;
  }
  if (sessionsResult.error) {
    throw sessionsResult.error;
  }

  const weekLogs = weekLogsResult.data || [];
  const hoursThisWeek = sumHours(weekLogs);
  const estimatedPayroll = computePayrollLiabilityTotals(
    weekLogs,
    orgSettings,
  ).grossPay;
  const pendingApproval = weekLogs.filter(
    (row) => (row.approval_status || "pending") === "pending",
  ).length;
  const employeesWorking = new Set(
    (sessionsResult.data || [])
      .map((row) => row.supabase_user_id)
      .filter(Boolean),
  ).size;

  return buildTimeLogsSummaryMetrics({
    hoursThisWeek,
    employeesWorking,
    pendingApproval,
    estimatedPayroll,
  });
}
